<!-- src/apps/secret/components/SecretReceiptTableItem.vue -->

<!--
  Console-style receipt item for dashboard.
  Monospace precision design matching SecretLinksTableRowConsole.
  Uses tree-style metadata display with ASCII characters.

  ENCRYPTION SIGNAL SYSTEM (Three-Layer):
  All secrets are encrypted (EO). Some also have passphrase protection (EAP).
  The UI distinguishes these with redundant visual cues:

  Layer 1 - Persistent iconography:
    - EO:  Single padlock icon (mid-gray)
    - EAP: Padlock + checkmark badge (lock-check icon)

  Layer 2 - Visual hierarchy:
    - Same saturation for both (no downgrading EO)

  Layer 3 - On-demand info:
    - Hover/tap shows tooltip with explanation
-->

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
import OIcon from '@/shared/components/icons/OIcon.vue';
import type { ReceiptList } from '@/schemas/shapes/v3/receipt';
import { formatRelativeTimeToNow } from '@/utils/format/relative-time';
import { formatTTL } from '@/utils/formatters';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { ref, computed } from 'vue';

const router = useRouter();
const { t } = useI18n();

interface Props {
  secretReceipt: ReceiptList;
  /** Row index (1-based) for visual reference */
  index: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  copy: [];
}>();

// Track if this row's content was copied
const isCopied = ref(false);

// Layer 3: Encryption tooltip visibility
const showEncryptionTooltip = ref(false);

const bootstrapStore = useBootstrapStore();
const { site_host, link_host } = storeToRefs(bootstrapStore);

// Create shareable link with proper domain
const shareLink = computed(() => {
  const shareDomain = props.secretReceipt.share_domain ?? link_host.value;
  return `https://${shareDomain}/secret/${props.secretReceipt.secret_shortid}`;
});

const handleCopy = async () => {
  if (!isActive.value) return;
  try {
    await navigator.clipboard.writeText(shareLink.value);
    isCopied.value = true;
    emit('copy');
    setTimeout(() => {
      isCopied.value = false;
    }, 1500);
  } catch (err) {
    console.error('Failed to copy text: ', err);
  }
};

// Format creation date
const formattedDate = computed(() =>
  formatRelativeTimeToNow(props.secretReceipt.created, { addSuffix: true })
);

// Compute security status and time remaining
const hasPassphrase = computed(() => props.secretReceipt.has_passphrase);
const timeRemaining = computed(() => formatTTL(props.secretReceipt.secret_ttl));

// Secret state computations
const isExpired = computed(() => props.secretReceipt.is_expired || props.secretReceipt.secret_ttl <= 0);
const isBurned = computed(() => props.secretReceipt.is_burned);
const isPreviewed = computed(() => props.secretReceipt.is_previewed);
const isRevealed = computed(() => props.secretReceipt.is_revealed);
const isDestroyed = computed(() => props.secretReceipt.is_destroyed);

// Calculate TTL percentage for urgency
const getTtlPercentage = computed(() => {
  const maxTtl = 604800; // 7 days
  return Math.floor((props.secretReceipt.secret_ttl / maxTtl) * 100);
});

/**
 * Determine item state based on receipt state.
 * Priority: burned > revealed > expired > previewed > new
 *
 * Burned and revealed must take precedence over expired/destroyed because
 * the backend sets multiple flags simultaneously:
 * - Burned secrets have is_burned=true AND is_destroyed=true AND secret_ttl=0
 * - Revealed secrets have is_revealed=true AND is_destroyed=true AND secret_ttl=-1
 *
 * The UI should show the actual state (burned/revealed) not the side effect.
 */
const itemState = computed((): 'new' | 'previewed' | 'revealed' | 'burned' | 'expired' => {
  if (isBurned.value) return 'burned';
  if (isRevealed.value) return 'revealed';
  if (isExpired.value || isDestroyed.value) return 'expired';
  if (isPreviewed.value) return 'previewed';
  return 'new';
});

// Console-style status configuration
const statusConfig = computed(() => {
  switch (itemState.value) {
    case 'expired':
      return {
        symbol: '○', // open circle
        label: t('web.STATUS.expired').toUpperCase(),
        colorClass: 'text-day-ink-faint dark:text-night-ink-faint',
      };
    case 'burned':
      return {
        symbol: '✕', // multiplication sign (X)
        label: t('web.STATUS.burned').toUpperCase(),
        colorClass: 'text-red-600 dark:text-red-400',
      };
    case 'revealed':
      return {
        symbol: '✓', // check mark
        label: t('web.STATUS.revealed').toUpperCase(),
        colorClass: 'text-day-ink-faint dark:text-night-ink-faint',
      };
    case 'previewed':
      return {
        symbol: '◐', // half-filled circle
        label: t('web.STATUS.previewed').toUpperCase(),
        colorClass: 'text-amber-600 dark:text-amber-400',
      };
    default: // new
      return {
        symbol: '●', // filled circle
        label: t('web.STATUS.new').toUpperCase(),
        colorClass: 'text-emerald-600 dark:text-emerald-400',
      };
  }
});

// Display key (truncated shortid)
const displayKey = computed(() => {
  const shortid = props.secretReceipt.secret_shortid;
  if (!shortid) return '';
  if (shortid.length <= 4) return shortid;
  return shortid.slice(0, 4);
});

// Check if secret is still actionable
const isActive = computed(() => itemState.value === 'new' || itemState.value === 'previewed');

// Check if this is a terminal state (no actions available)
const isTerminal = computed(() => !isActive.value);

// Terminal state timestamp: use action-specific date when available
const terminalActionDate = computed(() => {
  if (itemState.value === 'revealed' && props.secretReceipt.revealed) {
    return props.secretReceipt.revealed;
  }
  if (itemState.value === 'burned' && props.secretReceipt.burned) {
    return props.secretReceipt.burned;
  }
  return props.secretReceipt.created;
});

const formattedTerminalDate = computed(() =>
  formatRelativeTimeToNow(terminalActionDate.value, { addSuffix: true })
);

// Time remaining urgency
const isUrgent = computed(() => getTtlPercentage.value <= 25 && isActive.value);

// Whether the secret was created on a different domain
const hasDifferentDomain = computed(
  () => !!props.secretReceipt.share_domain && props.secretReceipt.share_domain !== site_host.value
);

// Receipt route for this record
const receiptRoute = computed(() => ({
  name: 'Receipt link',
  params: { receiptIdentifier: props.secretReceipt.identifier }
}));

// Navigate to receipt when clicking the row background
const handleRowClick = () => {
  router.push(receiptRoute.value);
};

const rowClasses = computed(() => ['font-mono text-sm', isTerminal.value && 'opacity-60']);
</script>

<template>
  <!--
    Figma Make "Recentes" (ago/2026): each receipt is its own white card on the
    tinted page — square, hairline border, one soft shadow — instead of a row
    inside a shared bordered box separated by rules. The card is what lets the
    oversized identifier watermark sit behind the row without bleeding into its
    neighbours.
  -->
  <li
    :class="rowClasses"
    class="group/row relative cursor-pointer overflow-hidden border border-day-line-2 bg-day px-5 py-[18px] shadow-[0_2px_10px_rgba(13,21,55,0.05)] transition-colors duration-150 hover:bg-day-100/60 dark:border-white/[0.13] dark:bg-night-100 dark:shadow-none dark:hover:bg-night-200/60"
    @click="handleRowClick">
    <!-- Content wrapper: contains everything, for watermark positioning -->
    <div class="relative">
      <!--
        Background watermark: the oversized shortid. The mock anchors it to the
        RIGHT (behind the action cluster), not centred — that is what keeps it
        clear of the #n / status / id text on the left, which would otherwise
        sit on top of it. Hidden on mobile, where there is no room to the right.
      -->
      <div
        class="pointer-events-none absolute inset-y-0 right-[130px] hidden select-none items-center sm:flex"
        aria-hidden="true">
        <span
          :class="[
            'font-mono text-[54px] font-bold uppercase leading-none tracking-[0.12em] transition-colors duration-150',
            isTerminal
              ? 'text-day-ink/[0.04] group-hover/row:text-day-ink/[0.07] dark:text-white/[0.03] dark:group-hover/row:text-white/[0.06]'
              : 'text-day-ink/[0.05] group-hover/row:text-day-ink/[0.09] dark:text-white/[0.04] dark:group-hover/row:text-white/[0.08]',
          ]">
          {{ displayKey }}
        </span>
      </div>

      <!-- Header line: #N  padlock  STATUS  Memo/ID  ·  actions -->
      <div class="relative flex flex-wrap items-center gap-x-3.5 gap-y-2">
        <!-- Index prefix (links to receipt) -->
        <router-link
          :to="receiptRoute"
          :aria-label="`${t('web.receipt.view_receipt')} #${index}`"
          class="receipt-control shrink-0 cursor-pointer select-none text-xs text-day-ink-faint no-underline hover:underline dark:text-night-ink-faint"
          @click.stop>
          #{{ index }}
        </router-link>

        <!--
          LAYER 1 & 2: Encryption iconography
          - All items show padlock (EO baseline)
          - EAP items add lock-check icon
        -->
        <div
          class="group/encrypt receipt-control relative flex-shrink-0 text-day-ink-muted dark:text-night-ink-muted"
          @mouseenter="showEncryptionTooltip = true"
          @mouseleave="showEncryptionTooltip = false"
          @focus="showEncryptionTooltip = true"
          @blur="showEncryptionTooltip = false"
          tabindex="0"
          role="img"
          :aria-label="
            hasPassphrase
              ? `${t('web.LABELS.encrypted')} + ${t('web.LABELS.passphrase_protected')}`
              : t('web.LABELS.encrypted')
          ">
          <!-- Padlock icon (always shown, same saturation for EO and EAP) -->
          <OIcon
            v-if="hasPassphrase"
            collection="tabler"
            name="lock-check"
            class="size-3.5" />
          <OIcon
            v-else
            collection="tabler"
            name="lock"
            class="size-3.5" />

          <!-- LAYER 3: Tooltip on hover/focus -->
          <Transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-1">
            <div
              v-if="showEncryptionTooltip"
              class="absolute -top-8 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap bg-day-ink px-2 py-1 text-xs font-medium text-white shadow-lg dark:bg-night-200 dark:text-night-ink">
              <template v-if="hasPassphrase">
                {{ t('web.LABELS.encrypted') }}
                <span class="text-teal-300"> + {{ t('web.LABELS.passphrase_protected') }}</span>
              </template>
              <template v-else>
                {{ t('web.LABELS.encrypted') }}
              </template>
              <!-- Tooltip arrow -->
              <div
                class="absolute -bottom-1 left-1/2 size-2 -translate-x-1/2 rotate-45 bg-day-ink dark:bg-night-200"
                aria-hidden="true"></div>
            </div>
          </Transition>
        </div>

        <!-- Status label -->
        <span
          data-testid="receipt-status"
          :class="['shrink-0 text-xs font-semibold tracking-[0.05em]', statusConfig.colorClass]">
          {{ statusConfig.label }}
        </span>

        <!-- Memo or shortid display -->
        <div class="min-w-0 flex-1">
          <span
            v-if="secretReceipt.memo"
            :class="[
              'block truncate text-xs',
              isActive
                ? 'text-day-ink dark:text-night-ink'
                : 'text-day-ink-muted dark:text-night-ink-muted',
            ]">
            {{ secretReceipt.memo }}
          </span>
          <span
            v-else
            class="text-xs text-day-ink-faint dark:text-night-ink-faint">
            {{ displayKey }}
          </span>
        </div>

        <!-- Actions (header line, right side) — stop propagation to prevent row click -->
        <div
          v-if="isActive"
          class="relative z-[1] flex flex-shrink-0 items-center gap-2"
          @click.stop>
          <!-- Copy button: icon-only on mobile, bracketed label on sm+ -->
          <button
            type="button"
            @click="handleCopy"
            :class="[
              'receipt-control border p-1.5 font-mono text-[11px] tracking-[0.04em] transition-colors duration-150 sm:px-[11px] sm:py-1.5',
              isCopied
                ? 'border-emerald-600/40 bg-emerald-600/[0.06] text-emerald-600 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-400'
                : 'border-day-line-2 bg-day text-day-ink-muted hover:bg-day-100 dark:border-white/[0.13] dark:bg-night-100 dark:text-night-ink-muted dark:hover:bg-night-200',
            ]"
            :title="t('web.LABELS.copy_to_clipboard')">
            <!-- Mobile: icon only -->
            <OIcon
              v-if="isCopied"
              collection="heroicons"
              name="check"
              class="size-4 sm:hidden" />
            <OIcon
              v-else
              collection="heroicons"
              name="clipboard"
              class="size-4 sm:hidden" />
            <!-- Desktop: text label -->
            <span class="hidden sm:inline">
              {{ isCopied ? '[ COPIED ]' : '[ COPY ]' }}
            </span>
          </button>

          <!-- Open link button: icon-only on mobile, bracketed label on sm+ -->
          <a
            :href="shareLink"
            target="_blank"
            rel="noopener noreferrer"
            class="receipt-control border border-day-line-2 bg-day p-1.5 font-mono text-[11px] tracking-[0.04em] text-day-ink-muted transition-colors duration-150 hover:bg-day-100 sm:px-[11px] sm:py-1.5 dark:border-white/[0.13] dark:bg-night-100 dark:text-night-ink-muted dark:hover:bg-night-200"
            :title="t('web.COMMON.view_secret')"
            @click.stop>
            <!-- Mobile: icon only -->
            <OIcon
              collection="heroicons"
              name="arrow-top-right-on-square"
              class="size-4 sm:hidden" />
            <!-- Desktop: text label -->
            <span class="hidden sm:inline">[ OPEN &#8599; ]</span>
          </a>

          <!-- Burn button: icon-only on mobile, bracketed label on sm+ -->
          <router-link
            :to="{ name: 'Burn secret', params: { receiptIdentifier: secretReceipt.identifier } }"
            class="receipt-control border border-red-500/35 bg-day p-1.5 font-mono text-[11px] tracking-[0.04em] text-red-600 transition-colors duration-150 hover:bg-red-500/[0.06] sm:px-[11px] sm:py-1.5 dark:border-red-500/40 dark:bg-night-100 dark:text-red-400 dark:hover:bg-red-500/10"
            :title="t('web.COMMON.burn')"
            @click.stop>
            <!-- Mobile: icon only -->
            <OIcon
              collection="heroicons"
              name="fire"
              class="size-4 sm:hidden" />
            <!-- Desktop: text label -->
            <span class="hidden sm:inline">[ BURN ]</span>
          </router-link>
        </div>
      </div>

      <!-- Metadata tree (full tree for active/previewed, minimal for terminal) -->
      <div class="relative mt-2.5 space-y-1 text-xs text-day-ink-faint dark:text-night-ink-faint">
        <template v-if="isActive">
          <!-- Domain line (only when share domain differs from site host) -->
          <div
            v-if="hasDifferentDomain"
            class="flex items-center">
            <span
              class="mr-2 select-none text-day-line-2 dark:text-white/20"
              aria-hidden="true"
              >&#9500;&#9472;</span
            >
            <span>via:</span>
            <span class="ml-1 text-day-ink-muted dark:text-night-ink-muted">{{ secretReceipt.share_domain }}</span>
          </div>

          <!-- Recipients line (if shown) -->
          <div
            v-if="secretReceipt.show_recipients && secretReceipt.recipients"
            class="flex items-center">
            <span
              class="mr-2 select-none text-day-line-2 dark:text-white/20"
              aria-hidden="true"
              >&#9500;&#9472;</span
            >
            <span>to:</span>
            <span class="ml-1 text-day-ink-muted dark:text-night-ink-muted">{{ secretReceipt.recipients }}</span>
          </div>

          <!-- Expires line with the relative timestamp on the right -->
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center">
              <span
                class="mr-2 select-none text-day-line-2 dark:text-white/20"
                aria-hidden="true"
                >&#9492;&#9472;</span
              >
              <span>expires:</span>
              <span
                :class="[
                  'ml-1 font-medium',
                  isUrgent
                    ? 'font-semibold text-amber-600 dark:text-amber-400'
                    : 'text-day-ink-muted dark:text-night-ink-muted',
                ]">
                {{ timeRemaining }}
              </span>
            </div>

            <!-- Timestamp -->
            <span
              data-test-id="created-timestamp"
              class="shrink-0 text-[11px]">
              <time :datetime="secretReceipt.created.toISOString()">
                {{ formattedDate }}
              </time>
            </span>
          </div>
        </template>

        <!-- Terminal states: minimal info -->
        <template v-else>
          <!-- Domain line (only when share domain differs from site host) -->
          <div
            v-if="hasDifferentDomain"
            class="flex items-center">
            <span
              class="mr-2 select-none text-day-line-2 dark:text-white/20"
              aria-hidden="true"
              >&#9500;&#9472;</span
            >
            <span>via:</span>
            <span class="ml-1 text-day-ink-muted dark:text-night-ink-muted">{{ secretReceipt.share_domain }}</span>
          </div>

          <div class="flex items-center">
            <span
              class="mr-2 select-none text-day-line-2 dark:text-white/20"
              aria-hidden="true"
              >&#9492;&#9472;</span
            >
            <span v-if="itemState === 'revealed'">viewed:</span>
            <span v-else-if="itemState === 'burned'">destroyed:</span>
            <span v-else>expired:</span>
            <span class="ml-1 text-day-ink-muted dark:text-night-ink-muted">
              <time :datetime="terminalActionDate.toISOString()">
                {{ formattedTerminalDate }}
              </time>
            </span>
          </div>
        </template>
      </div>
    </div>
    <!-- /Content wrapper -->
  </li>
</template>

<style scoped>
  /*
   * Same leak SecretComposer documents: reveal-page components ship a bare
   * `:focus { outline: 2px solid currentColor }` in <style scoped>, whose
   * unscoped half leaks app-wide and outranks Tailwind's focus:outline-none
   * (unlayered CSS vs @layer utilities). On these cards it drew a hard box
   * around [COPY]/[OPEN]/[BURN] on a plain mouse click. Kill the leaked
   * outline; the focus-visible ring below keeps the keyboard affordance.
   */
  .receipt-control:focus {
    outline: none;
  }

  .receipt-control:focus-visible {
    outline: 2px solid var(--color-brand-600);
    outline-offset: 2px;
  }
</style>
